<template>
  <div class="product-transactions-section" v-if="visible">
    <div class="section-header">
      <h3 class="section-title">
        <i class="fas fa-chart-line" style="width: 18px; height: 18px;"></i>
        Historique des mouvements de stock
      </h3>
      <BaseButton @click="reload" class="btn-refresh" :disabled="loading" title="Actualiser">
        <i class="fas fa-sync" style="width: 16px; height: 16px;"></i>
      </BaseButton>
    </div>

    <div v-if="loading" class="transactions-loading">
      <div class="spinner"></div>
      <p>Chargement des transactions...</p>
    </div>
    <div v-else-if="transactions.length === 0" class="no-transactions">
      <i class="fas fa-circle-info" style="width: 48px; height: 48px; font-size: 48px;"></i>
      <p>Aucun mouvement de stock enregistré</p>
    </div>
    <div v-else>
      <div class="product-transactions-table-container">
        <table class="product-transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Quantité</th>
              <th>Stock avant</th>
              <th>Stock après</th>
              <th>Utilisateur</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in transactions" :key="t.id">
              <td>{{ formatDate(t.transaction_date) }}</td>
              <td>
                <BaseBadge :class="['transaction-type-badge', mapTypeClass(t.transaction_type)]">
                  {{ mapTypeLabel(t.transaction_type) }}
                </BaseBadge>
              </td>
              <td :class="(t.quantity_change || t.quantity) >= 0 ? 'quantity-positive' : 'quantity-negative'">
                {{ (t.quantity_change || t.quantity) >= 0 ? '+' : '' }}{{ t.quantity_change ?? t.quantity }}
              </td>
              <td>{{ t.quantity_before ?? '-' }}</td>
              <td>{{ t.quantity_after ?? '-' }}</td>
              <td>{{ t.user?.name || '-' }}</td>
              <td class="transaction-notes">{{ t.notes || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationComponent v-if="pagination.totalPages > 1" :current-page="pagination.currentPage"
        :per-page="pagination.perPage" :total="pagination.total"
        :to="Math.min(pagination.currentPage * pagination.perPage, pagination.total)" @page-change="goToPage" />
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue'
import { productsService } from '@/services'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'

export default {
  name: 'ProductStockTransactions',
  components: {
    PaginationComponent,
    BaseBadge
  },
  props: {
    productId: {
      type: [Number, String],
      default: null
    },
    perPage: {
      type: Number,
      default: 8
    }
  },
  setup(props) {
    const transactions = ref([])
    const loading = ref(false)
    const pagination = ref({ currentPage: 1, totalPages: 1, total: 0, perPage: props.perPage })
    const visible = computed(() => !!props.productId)

    const loadTransactions = async (page = 1, perPage = props.perPage) => {
      if (!props.productId) {
        transactions.value = []
        return
      }
      loading.value = true
      try {
        const response = await productsService.getProductTransactions(props.productId, { per_page: perPage, page })
        // Extraction des transactions et pagination à partir de la réponse
        transactions.value = response.data?.data || []

        const meta = response.data?.meta || {}
        pagination.value.total = meta.total || transactions.value.length
        pagination.value.perPage = meta.per_page || perPage
        pagination.value.currentPage = page
        pagination.value.totalPages = Math.ceil(pagination.value.total / perPage)
      } catch (e) {
        transactions.value = []
      } finally {
        loading.value = false
      }
    }

    const reload = () => loadTransactions(pagination.value.currentPage, props.perPage)
    const goToPage = (page) => loadTransactions(page, props.perPage)

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
    const mapTypeLabel = (type) => {
      const labels = { purchase: 'Achat', sale: 'Vente', adjustment: 'Ajustement', return: 'Retour', damage: 'Perte/Dommage', transfer: 'Transfert', withdrawal: 'Retrait de stock' }
      return labels[type] || type
    }
    const mapTypeClass = (type) => {
      const classes = { purchase: 'transaction-purchase', sale: 'transaction-sale', adjustment: 'transaction-adjustment', return: 'transaction-return', damage: 'transaction-damage', transfer: 'transaction-transfer', withdrawal: 'transaction-withdrawal' }
      return classes[type] || ''
    }

    watch(() => props.productId, () => loadTransactions(1, props.perPage))
    onMounted(() => loadTransactions(1, props.perPage ))

    return { transactions, loading, pagination, visible, reload, goToPage, formatDate, mapTypeLabel, mapTypeClass }
  }
}
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #334155;
  margin: 0;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #0A5C6B;
  color: #0A5C6B;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.transactions-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0A5C6B;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0)
  }

  100% {
    transform: rotate(360deg)
  }
}

.product-transactions-table-container {
  overflow-x: auto;
  margin-top: 16px;
}

.product-transactions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.product-transactions-table th,
.product-transactions-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.product-transactions-table th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #334155;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-transactions-table td {
  color: #475569;
}

.product-transactions-table tr:hover td {
  background-color: #f8fafc;
}

.transaction-type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.transaction-purchase {
  background: #dcfce7;
  color: #166534;
}

.transaction-sale {
  background: #dbeafe;
  color: #1e40af;
}

.transaction-adjustment {
  background: #fef3c7;
  color: #92400e;
}

.transaction-return {
  background: #e0e7ff;
  color: #4338ca;
}

.transaction-damage {
  background: #fee2e2;
  color: #991b1b;
}

.transaction-transfer {
  background: #f3e8ff;
  color: #7e22ce;
}

.quantity-positive {
  color: #166534;
  font-weight: 600;
}

.quantity-negative {
  color: #991b1b;
  font-weight: 600;
}

.transaction-notes {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-transactions {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  color: #9ca3af;
}
</style>
